/**
 * Which colour is "the foreground" in the space being rendered — the ONE truth
 * pairing a drawing-area background with the ink AutoCAD colour 7 draws in.
 *
 * A leaf module on purpose: it imports nothing, so `rendering/colorResolution`
 * (the resolver that consumes the foreground), `scene/PaperSpaceLayer` (which
 * paints the background it is derived from) and `Scene3DStage` (which paints
 * the drawing area) can all read the same constants without a cycle.
 */

/**
 * # Background-dependent foreground (ACI 7 is "black/white", not "white")
 *
 * ## The rule
 *
 * AutoCAD colour index 7 is not a colour, it is *the contrast colour of the
 * drawing-area background*: white on a dark background, BLACK on a light one.
 * Every other index is background-independent. `ezdxf`'s renderer implements
 * exactly this and is the reference this module follows:
 *
 *   * `ezdxf.addons.drawing.properties.LayoutProperties.set_colors` —
 *     `default_color = "#ffffff" if has_dark_background else "#000000"`;
 *   * `RenderContext._aci_to_true_color` — `if aci == 7: return
 *     current_layout_properties.default_color`;
 *   * `LayerProperties.get_entity_color_from_layer(fg)` — a layer whose own
 *     ACI is 7 hands the entity that same `fg`;
 *   * `LayoutProperties.from_layout` — `MODEL_SPACE_BG_COLOR = "#212830"`
 *     (dark) for `"Model"`, `PAPER_SPACE_BG_COLOR = "#ffffff"` for every
 *     paper-space layout;
 *   * and `ezdxf.colors` ships the two AutoCAD 2020 default palettes side by
 *     side: `DXF_DEFAULT_COLORS[7] == 0xFFFFFF` (Uniform background 33,40,48)
 *     vs `DXF_DEFAULT_PAPERSPACE_COLORS[7] == 0x000000` (Sheet/layout Uniform
 *     background White).
 *
 * ## The bug this closes
 *
 * `scene/PaperSpaceLayer.tsx` paints an OPAQUE WHITE sheet in a layout tab,
 * but this module resolved ACI 7 through `aciColorMap.ts`, whose entry is the
 * hard `#FFFFFF` ("we use white for dark UI"). So every colour-7 object that
 * lands on the sheet — which in a real drawing is the sheet frame, the title
 * block, the ACAD_TABLEs and ALL of their text — was painted white on white
 * and was invisible, while the same objects sitting OUTSIDE the sheet stayed
 * legible on the dark surround. Measured on `apps/web/public/abi1-test.dxf`
 * layout «АБИ 1»: the on-sheet INSERT `1AD`, ACAD_TABLE `1458`, MTEXT `97E5` /
 * `97ED` and POLYLINE `8F0B` painted ZERO pixels; the off-sheet ACAD_TABLE
 * `6126` and MTEXT `614B` painted normally. The PDF plot path had already
 * closed the same hole on its side (`print/pdf/pdfTransform.ts resolvePlotRgb`
 * — "any white result becomes black so the geometry is visible on the white
 * PDF page"); screen paper space now agrees with the plot.
 *
 * ## Declared gap
 *
 * AutoCAD's paper-space palette also darkens the deep-blue band and re-ramps
 * the greys (`DXF_DEFAULT_PAPERSPACE_COLORS` differs from `DXF_DEFAULT_COLORS`
 * at indices 109, 119, 129, 139, 147, 149, 157, 159, 167, 169, 177, 179, 187,
 * 189, 197, 199, 209, 219, 229, 239, 249 and 251-255 as well as at 7). Only
 * index 7 makes geometry DISAPPEAR, and this repo's `ACI_COLORS` is already a
 * different palette variant from ezdxf's for 197 of its 255 entries, so
 * swapping in a second full table is a separate slice. Indices other than 7
 * therefore resolve identically in both spaces for now.
 */

/** Drawing-area background of MODEL space (the `<Canvas>` CSS background). */
export const MODEL_SPACE_BACKGROUND_HEX = "#1f1f23";

/** What ACI 7 resolves to on a DARK background — `ezdxf`'s `"#ffffff"`. */
export const MODEL_SPACE_FOREGROUND_HEX = "#ffffff";

/** What ACI 7 resolves to on a LIGHT background — `ezdxf`'s `"#000000"`. */
export const PAPER_SPACE_FOREGROUND_HEX = "#000000";

/**
 * Background of a PAPER-space layout — white paper, the same `0xffffff`
 * `scene/PaperSpaceLayer.tsx` fills the sheet with, and ezdxf's
 * `PAPER_SPACE_BG_COLOR`. This is the surface the layout foreground contrasts
 * against, because the sheet is where a layout's geometry lives.
 */
export const PAPER_SPACE_BACKGROUND_HEX = "#ffffff";

/**
 * The layout drawing area AROUND the sheet.
 *
 * AutoCAD's layout tab is the classic "white sheet on a grey desk": the paper
 * is white and the area beyond it is a mid grey, so the sheet edge is visible
 * without a margin frame (this renderer draws no printable-area frame — see
 * `scene/PaperSpaceLayer.tsx`, «отступ убрать»). It MUST stay a LIGHT colour:
 * paper space resolves colour 7 to black for the sheet, and paper-space
 * geometry is allowed to sit outside the sheet (layout «АБИ 1» of
 * `apps/web/public/abi1-test.dxf` keeps three ACAD_TABLEs and two MTEXTs
 * there), so a dark surround would just move the white-on-white problem
 * outside the page instead of fixing it. `isDarkBackground` of this value is
 * asserted false by `__tests__/colorResolution.paperSpace.test.ts`.
 */
export const PAPER_SPACE_SURROUND_HEX = "#9a9aa0";

/**
 * Perceived luminance of a `#rrggbb` in `[0, 1]`, the exact formula
 * `ezdxf.colors.luminance` uses (`sqrt(0.299r² + 0.587g² + 0.114b²)`,
 * rounded to 3 decimals). Unparsable input reads as fully dark so the
 * foreground degrades to today's white rather than flipping to black.
 */
function backgroundLuminance(backgroundHex: string): number {
  const parsed = /^#?([0-9a-f]{6})$/i.exec(backgroundHex.trim());
  if (parsed === null) return 0;
  const r = Number.parseInt(parsed[1].slice(0, 2), 16) / 255;
  const g = Number.parseInt(parsed[1].slice(2, 4), 16) / 255;
  const b = Number.parseInt(parsed[1].slice(4, 6), 16) / 255;
  return (
    Math.round(
      Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b) * 1000,
    ) / 1000
  );
}

/**
 * `ezdxf.addons.drawing.properties.is_dark_color` — luminance ≤ 0.2. A value
 * that is not a `#rrggbb` reads as fully dark, so the foreground degrades to
 * the historical `#ffffff` rather than flipping the whole drawing to black.
 */
export function isDarkBackground(backgroundHex: string): boolean {
  return backgroundLuminance(backgroundHex) <= 0.2;
}

/**
 * The contrast colour ACI 7 resolves to on `backgroundHex` —
 * `LayoutProperties.set_colors`' `default_color`.
 */
export function foregroundHexForBackground(backgroundHex: string): string {
  return isDarkBackground(backgroundHex)
    ? MODEL_SPACE_FOREGROUND_HEX
    : PAPER_SPACE_FOREGROUND_HEX;
}

/**
 * The DRAWING-AREA background (the `<Canvas>` CSS colour, which is what the
 * user sees wherever the transparent WebGL buffer is untouched): the dark
 * editor background in model space, the grey desk around the sheet in a
 * layout. `spaceKind` is the `DrawingSpace["kind"]` string the stage already
 * threads (`Scene3DStage.tsx`'s `activeSpaceKind`); anything that is not
 * `"layout"` (including `undefined`) is model space.
 */
export function backgroundHexForSpaceKind(
  spaceKind: string | undefined,
): string {
  return spaceKind === "layout"
    ? PAPER_SPACE_SURROUND_HEX
    : MODEL_SPACE_BACKGROUND_HEX;
}

/**
 * Both answers, DERIVED from their backgrounds ONCE at module load.
 *
 * The derivation is the point (a foreground is never named on its own), but it
 * must not run per entity: `foregroundHexForSpaceKind` is on the bulk-load hot
 * path — `adapters/adapterContext.ts` asks it for EVERY renderable, and a
 * 685k-entity import would otherwise pay a regex + three `parseInt`s + a
 * `Math.sqrt` per entity to re-derive one of two constants.
 */
const FOREGROUND_BY_SPACE_KIND: Readonly<{ layout: string; model: string }> = {
  layout: foregroundHexForBackground(PAPER_SPACE_BACKGROUND_HEX),
  model: foregroundHexForBackground(MODEL_SPACE_BACKGROUND_HEX),
};

/**
 * `LayoutProperties.from_layout` — the foreground colour 7 resolves to in this
 * space. Derived from the surface a layout's geometry actually sits on (the
 * white SHEET), not from the surround; both layout backgrounds are light, so
 * the one answer is legible on both (see {@link PAPER_SPACE_SURROUND_HEX}).
 *
 * `spaceKind` is a `DrawingSpace["kind"]`; anything that is not `"layout"`
 * (`"model"`, the transient `"block-edit"` / `"block-test"` / `"refedit"`
 * sessions, an unknown kind, `undefined`) draws on the dark editor background.
 */
export function foregroundHexForSpaceKind(
  spaceKind: string | undefined,
): string {
  return spaceKind === "layout"
    ? FOREGROUND_BY_SPACE_KIND.layout
    : FOREGROUND_BY_SPACE_KIND.model;
}

/**
 * The MODEL-space background as normalised `[0,1]` RGB, for the WebGL callers
 * that clear a buffer rather than set a CSS colour
 * (`rendering/viewportRenderPass.ts`: the per-VP `gl.clearColor` and the
 * clipped-VP interior fill).
 *
 * Derived from {@link MODEL_SPACE_BACKGROUND_HEX} so the viewport background
 * and the foreground a model entity resolves to CANNOT drift apart — that
 * drift is this module's whole subject, and the VP clear used to carry its own
 * `0.122, 0.122, 0.137` literal with only a comment tying it to the hex.
 * Quantised to 8 bits the derived values are byte-identical to those literals
 * (31, 31, 35 = `#1f1f23`); pinned by `colorResolution.paperSpace.test.ts`.
 */
export const MODEL_SPACE_BACKGROUND_RGB01: Readonly<{
  r: number;
  g: number;
  b: number;
}> = {
  r: 0x1f / 255,
  g: 0x1f / 255,
  b: 0x23 / 255,
};

/**
 * `DrawingSpace["kind"]` of model space — the kind every non-active space a
 * render pass carries has, and the kind whose (dark background, white ink)
 * pair {@link foregroundHexForSpaceKind} returns for anything unrecognised.
 */
const MODEL_SPACE_KIND = "model";

/**
 * # A layout pass renders TWO spaces, so ONE foreground is not enough
 *
 * `foregroundHexForSpaceKind` above answers "what is the ink of the space the
 * user is IN". That is the right question for the sheet and the wrong one for
 * everything a layout shows through its VIEWPORTs: a paper layout materialises
 * BOTH its own entities AND model space in a single pass
 * (`scene/SceneBulkLoad.ts`'s `sourceEntities`, `scene/cacheDirectSpaces.ts`'s
 * `resolveRenderSpaceIds`), because the per-VP scissor passes MASK objects
 * that already exist in the shared scene — they lift nothing themselves.
 *
 * Those two halves sit on two DIFFERENT backgrounds:
 *
 *   * paper-space entities → the white sheet ({@link PAPER_SPACE_BACKGROUND_HEX},
 *     painted by `scene/PaperSpaceLayer.tsx`) or the light surround around it
 *     ({@link PAPER_SPACE_SURROUND_HEX}) — both light, ink BLACK;
 *   * model-space entities → the viewport window, which
 *     `rendering/viewportRenderPass.ts` clears to
 *     {@link MODEL_SPACE_BACKGROUND_HEX} before rendering the model scene
 *     through the VP camera (AutoCAD parity: model geometry shows on its OWN
 *     background, the paper does not bleed in) — dark, ink WHITE.
 *
 * Resolving the whole frame at the ACTIVE space's kind therefore painted every
 * model ACI-7 / BYLAYER-on-ACI-7 object BLACK onto that dark VP background —
 * WCAG contrast 1.28:1, the mirror image of the white-on-white sheet bug this
 * module was written to close (@codex round 15 P1,
 * `scene/buildAdapterCtxBase.ts:139`).
 *
 * # The rule
 *
 * An entity belongs to the ACTIVE space when its `spaceId` is the active one;
 * anything else a pass carries is model space by construction (a pass renders
 * the active space plus, behind a layout, model — `resolveRenderSpaceIds`).
 * The answer still goes through {@link foregroundHexForSpaceKind}, so the
 * (background, foreground) pair is chosen in ONE place and a caller can never
 * name a foreground on its own.
 *
 * Degradation: with `activeSpaceId` unknown (`undefined` — a hand-built
 * context, the PDF converters, a unit test) every entity reads as model space
 * and the answer is the historical `#ffffff`, so nothing that has not been
 * taught about spaces changes colour.
 */
export function foregroundHexForRenderedSpace(args: {
  /** `RenderableEntity["spaceId"]` / `DrawingEntity["spaceId"]`. */
  entitySpaceId: string;
  /** `DrawingDocument["activeSpaceId"]` for the pass, when known. */
  activeSpaceId: string | undefined;
  /** `DrawingSpace["kind"]` of that active space. */
  activeSpaceKind: string | undefined;
}): string {
  const inActiveSpace =
    args.activeSpaceId !== undefined &&
    args.entitySpaceId === args.activeSpaceId;
  return foregroundHexForSpaceKind(
    inActiveSpace ? args.activeSpaceKind : MODEL_SPACE_KIND,
  );
}
